import { countryName } from "@breenova/domain";
import { Alert, Button, Card, SegmentedControl, cn } from "@breenova/ui";
import {
  Bell,
  CalendarRange,
  ChevronRight,
  Download,
  LogOut,
  RefreshCw,
  ShieldCheck,
  UserRound,
  Users,
  Warehouse,
} from "lucide-react";
import type { ComponentType } from "react";
import { useState } from "react";
import { Link } from "react-router";

import { useAuth } from "@/auth/auth-context";
import { Avatar } from "@/components/app-shell";
import {
  CURRENT_BUILD,
  applyUpdate,
  buildLabel,
  checkForUpdate,
  useAppUpdate,
} from "@/lib/app-update";
import { downloadExport } from "@/lib/exports";
import { useTheme, type ThemePreference } from "@/lib/theme";
import { SettingsFrame } from "./settings-frame";

const THEME_OPTIONS: readonly { value: ThemePreference; label: string }[] = [
  { value: "system", label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

/**
 * "Your aviary": everything that is about the account rather than about the birds.
 *
 * Presented as a sheet with a close button rather than a tab, because it is somewhere
 * you visit and leave. A hub of links rather than one long screen: breeder details and
 * security are each a form that wants the breeder's whole attention, and burying a
 * password field under an export button is how it gets changed by accident.
 */
export function SettingsPage() {
  const { user, isAdmin, signOut } = useAuth();
  const { preference, setPreference } = useTheme();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function exportRegister(): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      await downloadExport("/api/v1/exports/birds.csv", "breenova-birds.csv");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The export failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col md:gap-lg">
      <header className="px-5 pb-4 pt-3.5 md:px-gutter-wide md:pb-0 md:pt-7.5">
        <h1 className="text-page-title text-on-surface md:text-page-title-wide">Me</h1>
      </header>

      <SettingsFrame>
        <section className="flex w-full flex-col gap-4">
          {error ? <Alert tone="error">{error}</Alert> : null}

          <div className="flex items-center gap-3.5 rounded-lg bg-primary-fixed p-lg text-on-primary-fixed">
            <Avatar
              name={user?.displayName}
              className="size-15.5 bg-inverse-primary text-disc-wide text-on-primary-fixed"
            />

            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-section-header-wide">
                {user?.displayName ?? "Your account"}
              </span>
              <span className="truncate text-subtitle">
                {[
                  user?.federationName,
                  user?.ringCode ? `breeder code ${user.ringCode}` : null,
                  user?.countryCode ? countryName(user.countryCode) : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || (user?.email ?? "—")}
              </span>
            </div>
          </div>

          {/*
            No `overflow-hidden` on this list: a two-line hint takes a row past 66px, and
            clipping it cuts the last row in half.

            Phone only. From `md` the rail carries the same sections and keeps them on
            screen inside each one — a list here as well would be the same navigation twice,
            and the one a breeder learns is whichever they happen to click first.
          */}
          <Card className="flex flex-col px-4 py-1 md:hidden">
            <SettingsLink
              to="/more/profile"
              icon={UserRound}
              label="Breeder details"
              hint="Name, federation, ring series"
            />
            <SettingsLink
              to="/more/locations"
              icon={Warehouse}
              label="Cages & locations"
              hint="The rooms, sections and cages birds are placed in"
            />
            {/* Reached from the season chip on the nests screen too, which is where the
                year actually turns. Here because it is setup a breeder goes looking for by
                name, and this is where the rest of the setup lives. */}
            <SettingsLink
              to="/breeding/seasons"
              icon={CalendarRange}
              label="Breeding seasons"
              hint="Open, close and choose which season is current"
              state={{ from: "/more" }}
            />
            <SettingsLink
              to="/more/notifications"
              icon={Bell}
              label="Notifications"
              hint="Hatch and ringing reminders"
            />
            <SettingsLink
              to="/more/security"
              icon={ShieldCheck}
              label="Security"
              hint="Password and sign-in"
            />
            {/* Not in the navigation: that is for the birds. This is where setup lives,
                and for most breeders the row is simply not here. */}
            {isAdmin ? (
              <SettingsLink
                to="/admin/users"
                icon={Users}
                label="People"
                hint="Let new breeders in, and choose who else may"
              />
            ) : null}
          </Card>

          <div className="flex flex-col gap-2">
            <span className="text-node text-on-surface-body">Appearance</span>
            <SegmentedControl
              label="Appearance"
              value={preference}
              options={THEME_OPTIONS}
              onChange={setPreference}
              className="flex w-full"
            />
          </div>

          <div className="flex gap-2.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => void exportRegister()}
              className={cn(
                "flex h-12.5 flex-1 items-center justify-center gap-2 rounded-control",
                "bg-on-surface text-label text-surface transition-opacity hover:opacity-90",
                "disabled:opacity-45",
              )}
            >
              <Download className="size-4.5" aria-hidden />
              {busy ? "Preparing…" : "Export birds"}
            </button>

            <button
              type="button"
              onClick={() => void signOut()}
              className={cn(
                "flex h-12.5 flex-1 items-center justify-center gap-2 rounded-control",
                "bg-surface-container-lowest text-label text-secondary-strong shadow-card",
                "transition-colors hover:bg-surface-container-low",
              )}
            >
              <LogOut className="size-4.5" aria-hidden />
              Sign out
            </button>
          </div>

          <AboutCard />

          <p className="text-caption text-on-surface-variant">
            BreeNova keeps your own breeding records. The documents it generates say so on
            their face: none of them is a permit or a document issued by a federation.
          </p>
        </section>
      </SettingsFrame>
    </div>
  );
}

/**
 * Which build this is, and the way onto a newer one.
 *
 * The build time is stored and served in UTC and shown here in the phone's own zone.
 */
function AboutCard() {
  const { status, latest } = useAppUpdate();
  const [updating, setUpdating] = useState(false);

  const builtAt = new Date(CURRENT_BUILD.builtAt).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const message: Record<typeof status, string | null> = {
    idle: null,
    checking: "Checking…",
    current: "You have the latest version.",
    available: latest
      ? `Build ${buildLabel(latest)} is ready to install.`
      : "A new version is ready to install.",
    failed: "Could not reach the server to check.",
  };

  return (
    <Card tone="recessed" flat className="flex flex-col gap-md p-lg">
      <h2 className="text-label-caps uppercase text-on-surface-variant">About this app</h2>

      <dl className="grid grid-cols-[auto_1fr] gap-x-lg gap-y-xs text-detail">
        <dt className="text-on-surface-variant">Version</dt>
        <dd className="text-on-surface" data-numeric>
          {CURRENT_BUILD.version}
        </dd>
        <dt className="text-on-surface-variant">Build</dt>
        <dd className="text-on-surface" data-numeric>
          {buildLabel(CURRENT_BUILD)}
        </dd>
        <dt className="text-on-surface-variant">Deployed</dt>
        <dd className="text-on-surface" data-numeric>
          {builtAt}
        </dd>
      </dl>

      {message[status] ? (
        <p role="status" className="text-detail text-on-surface-variant">
          {message[status]}
        </p>
      ) : null}

      {status === "available" ? (
        <Button
          variant="primary"
          className="self-start"
          disabled={updating}
          onClick={() => {
            setUpdating(true);
            void applyUpdate();
          }}
        >
          <RefreshCw className="size-4" />
          {updating ? "Updating…" : "Update now"}
        </Button>
      ) : (
        <Button
          variant="ink"
          className="self-start"
          disabled={status === "checking"}
          onClick={() => void checkForUpdate()}
        >
          <RefreshCw className="size-4" />
          Check for updates
        </Button>
      )}
    </Card>
  );
}

/** One row of the hub. A whole-row target, because a thumb is not a mouse pointer. */
function SettingsLink({
  to,
  icon: Icon,
  label,
  hint,
  state,
}: {
  to: string;
  icon: ComponentType<{ className?: string }>;
  label: string;
  hint: string;
  /**
   * Router state for a destination that lives in more than one place. Seasons hang off
   * the nests screen as well as this hub, and its back arrow has to return to whichever
   * one was actually used.
   */
  state?: unknown;
}) {
  return (
    <Link
      to={to}
      state={state}
      className={cn(
        "flex min-h-16.5 items-center gap-3 py-2.5",
        "border-b border-outline-variant last:border-b-0",
        "transition-colors hover:text-on-surface",
      )}
    >
      <span
        aria-hidden
        className="flex size-10 shrink-0 items-center justify-center rounded-full bg-surface-container text-on-primary-fixed-variant"
      >
        <Icon className="size-4.5" />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="text-label text-on-surface">{label}</span>
        <span className="text-caption text-on-surface-variant">{hint}</span>
      </span>

      <ChevronRight className="size-4.75 shrink-0 text-outline" aria-hidden />
    </Link>
  );
}
